/**
 * Mensagens de UI para fluxo de pagamento / consulta de pedido (login, permissão, sessão).
 */

export const LOGIN_HINT_KEYS = {
  PAYMENT_TRACKING: "payment_tracking",
  CHECKOUT: "checkout",
  ORDER_CONFIRMATION: "order_confirmation",
  ORDERS: "orders",
  ADMIN: "admin",
  DEFAULT: "default",
};

export const LOGIN_HINT_MESSAGES = {
  [LOGIN_HINT_KEYS.PAYMENT_TRACKING]:
    "Para acompanhar o pagamento em tempo real, entre na mesma conta usada no checkout.",
  [LOGIN_HINT_KEYS.CHECKOUT]:
    "Entre na sua conta para finalizar o pedido com segurança.",
  [LOGIN_HINT_KEYS.ORDER_CONFIRMATION]:
    "Faça login para ver os detalhes e a confirmação do seu pedido.",
  [LOGIN_HINT_KEYS.ORDERS]:
    "Acesse sua conta para ver o histórico de pedidos.",
  [LOGIN_HINT_KEYS.ADMIN]:
    "Área administrativa: faça login com uma conta autorizada para continuar.",
  [LOGIN_HINT_KEYS.DEFAULT]: "Faça login para continuar.",
};

/**
 * Define o tipo de aviso no login conforme a rota de origem.
 */
export function getLoginHintKeyFromPath(pathname) {
  const p = (pathname || "").toLowerCase();
  if (
    p.includes("/pending") ||
    p.includes("/pagamento/") ||
    p.includes("/payment-status")
  ) {
    return LOGIN_HINT_KEYS.PAYMENT_TRACKING;
  }
  if (
    p.includes("/checkout") ||
    p.includes("/order-summary") ||
    p.includes("/envio")
  ) {
    return LOGIN_HINT_KEYS.CHECKOUT;
  }
  if (p.includes("/confirmacao") || p.includes("/order-confirmation") || p.includes("/pedido/")) {
    return LOGIN_HINT_KEYS.ORDER_CONFIRMATION;
  }
  if (p.includes("/minha-conta/pedidos") || p.includes("/order-details") || p.includes("/pedido/")) {
    return LOGIN_HINT_KEYS.ORDERS;
  }
  if (p.includes("/admin") || p.includes("/notificationadmin")) {
    return LOGIN_HINT_KEYS.ADMIN;
  }
  return LOGIN_HINT_KEYS.DEFAULT;
}

/**
 * Traduz erros comuns do check-status / fetchWithAuth em mensagens claras para o usuário.
 */
export function messageForPaymentStatusError(error) {
  const raw = error?.message || "";
  const m = raw.toLowerCase();

  if (
    m.includes("não autorizado") ||
    m.includes("nao autorizado") ||
    m.includes("403")
  ) {
    return "Este pedido não está vinculado à sua conta. Entre com o e-mail usado na compra ou abra Meus pedidos na conta correta.";
  }

  if (
    m.includes("sessão") ||
    m.includes("sessao") ||
    m.includes("expirou") ||
    m.includes("faça login") ||
    m.includes("faca login") ||
    m.includes("401") ||
    m.includes("precisa estar logado")
  ) {
    return "Sua sessão expirou ou você não está logado. Faça login com a conta da compra para ver o status do pagamento.";
  }

  if (m.includes("não encontrado") || m.includes("nao encontrado") || m.includes("404")) {
    return "Não encontramos um pedido com essa referência. Confira o código (e-mail de confirmação ou tela Meus pedidos) e tente de novo.";
  }

  return null;
}

/** Texto curto exibido acima de formulários de consulta manual */
export const PAYMENT_CHECK_AUTH_NOTE =
  "Você precisa estar logado na conta em que o pedido foi feito. A consulta protege seus dados.";

const FAILURE_MESSAGE_MERCADO_PAGO =
  "O Mercado Pago não aprovou o pagamento. Revise os dados do cartão, limite ou experimente PIX; em seguida finalize de novo no checkout.";

const FAILURE_MESSAGE_PAYPAL =
  "O pagamento no PayPal não foi concluído. Você cancelou ou não autorizou a cobrança. Pode tentar de novo no checkout ou escolher PIX/cartão.";

const FAILURE_MESSAGE_GENERIC =
  "O pagamento não foi concluído. Você pode tentar de novo no checkout ou escolher outro meio de pagamento.";

/**
 * Mensagem da tela de falha/cancelamento conforme o gateway.
 * @param {string} [paymentMethod]
 * @param {string} [fallbackMessage]
 */
export function getPaymentFailureMessage(paymentMethod, fallbackMessage) {
  if (fallbackMessage) return fallbackMessage;
  const m = String(paymentMethod || "").toLowerCase();
  if (m === "paypal") return FAILURE_MESSAGE_PAYPAL;
  if (m === "pix" || m === "credit_card" || m === "mercadopago" || m === "card") {
    return FAILURE_MESSAGE_MERCADO_PAGO;
  }
  return FAILURE_MESSAGE_GENERIC;
}

/**
 * Parágrafos curtos antes do botão "Finalizar" no checkout (mesma linguagem do fluxo de pagamento).
 */
export function getCheckoutFinalizeHintParagraphs(paymentMethod) {
  const base = [
    "O pedido fica vinculado à conta em que você está logado agora. Para ver status e histórico, use sempre o mesmo acesso em Meus pedidos.",
  ];

  if (paymentMethod === "pix" || paymentMethod === "credit_card") {
    return [
      ...base,
      "Depois de pagar no Mercado Pago, volte com a sessão desta loja ativa: a página de pagamento pendente atualiza o status sozinha enquanto você estiver logado.",
      "A referência do pedido (ex.: order_…) também é enviada por e-mail — guarde para suporte ou para consultar em Meus pedidos.",
    ];
  }

  if (paymentMethod === "paypal") {
    return [
      ...base,
      "Você será enviado ao site do PayPal para autorizar. Depois, volte logado na mesma conta da loja para confirmarmos o pagamento.",
      "A referência do pedido (ex.: order_…) também é enviada por e-mail — guarde para suporte ou para consultar em Meus pedidos.",
    ];
  }

  if (paymentMethod === "direto") {
    return [
      ...base,
      "Após transferir o PIX, registre o comprovante em Meus pedidos (botão na lista) e aguarde a confirmação da loja no prazo informado.",
    ];
  }

  if (paymentMethod === "balcao") {
    return [
      ...base,
      "Na retirada, o pagamento é feito na loja; o status será atualizado pela equipe quando você buscar o pedido.",
    ];
  }

  return base;
}

/** Introdução em Meus pedidos — alinhada à privacidade e à referência */
export const ORDER_HISTORY_INTRO =
  "Aqui aparecem só os pedidos desta conta. A coluna Referência (ex.: order_…) é a mesma do e-mail e do Mercado Pago; use-a se precisar falar com o suporte.";

/** Estado vazio da lista filtrada */
export const ORDER_HISTORY_EMPTY_FILTERED =
  "Nenhum pedido com este filtro. Escolha outro status ou “Todos”.";

/** Estado vazio quando não há pedidos de jeito nenhum */
export const ORDER_HISTORY_EMPTY_NO_ORDERS =
  "Você ainda não tem pedidos nesta conta. Quando finalizar uma compra, ela aparecerá aqui com a referência e o status.";

/** Topo da página de detalhes de um pedido */
export const ORDER_DETAILS_INTRO =
  "Você está vendo um pedido desta conta. A referência abaixo é a mesma do e-mail e do Mercado Pago; use-a no suporte. O status atualiza após pagamento, confirmação manual ou envio.";

/** Pedido não encontrado / erro ao carregar (detalhes) */
export const ORDER_DETAILS_NOT_FOUND =
  "Não encontramos este pedido nesta conta ou houve um erro ao carregar. Verifique o link ou abra Meus pedidos.";

/** Resumo do pedido (antes do checkout) */
export const ORDER_SUMMARY_INTRO =
  "Confira itens e entrega. Na próxima etapa você escolhe o pagamento — o pedido será vinculado à conta em que estiver logado agora.";

/** Nota sobre totais no resumo */
export const ORDER_SUMMARY_TOTALS_NOTE =
  "Frete e descontos (por exemplo, PIX direto ao vendedor) são confirmados no checkout, com valores calculados de novo no servidor.";

/** Carrinho vazio / sem estado no resumo */
export const ORDER_SUMMARY_EMPTY_CART =
  "Não há itens para resumir ou os dados da etapa anterior expiraram. Volte ao carrinho e siga o fluxo até aqui de novo.";

/** Endereço obrigatório (entrega) */
export const ORDER_SUMMARY_ADDRESS_REQUIRED =
  "Para entrega é preciso de endereço e frete. Volte ao carrinho, calcule o frete e preencha os dados.";
